import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { 
  Shield, 
  Truck, 
  Headphones, 
  Award, 
  Users, 
  Globe,
  Smartphone,
  Tablet,
  Watch,
  Laptop,
  Camera,
  Gamepad
} from 'lucide-react';

const About = () => {
  const { colors } = useTheme();

  const features = [
    {
      icon: Shield,
      title: "Quality Guaranteed",
      description: "All our products come with warranty and quality assurance"
    },
    {
      icon: Truck,
      title: "Fast Delivery",
      description: "Free nationwide delivery within 24-48 hours"
    },
    {
      icon: Headphones,
      title: "24/7 Support",
      description: "Round-the-clock customer service and technical support"
    },
    {
      icon: Award,
      title: "Award Winning",
      description: "Recognized as the best tech retailer for 3 consecutive years"
    }
  ];

  const categories = [
    { icon: Smartphone, label: "Smart Phones", value: "smartphones", count: "500+" },
    { icon: Tablet, label: "Tablets", value: "tablets", count: "150+" },
    { icon: Watch, label: "Smart Watches", value: "smart-watches", count: "200+" },
    { icon: Laptop, label: "Laptops", value: "laptops", count: "300+" },
    { icon: Camera, label: "Drones", value: "drones", count: "80+" },
    { icon: Gamepad, label: "Games Consoles", value: "games-consoles", count: "120+" }
  ];

  const stats = [
    { number: "50,000+", label: "Happy Customers" },
    { number: "5+", label: "Years Experience" },
    { number: "1000+", label: "Products Available" },
    { number: "24/7", label: "Customer Support" }
  ];

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: colors.bg }}>
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h1 className="text-5xl md:text-6xl font-black leading-tight" style={{ color: colors.text }}>
                Welcome to{' '}
                <span style={{ color: colors.primary }}>JosiePhones</span>
              </h1>
              <p className="text-xl leading-relaxed" style={{ color: colors.textSecondary }}>
                Your trusted partner for cutting-edge technology and premium gadgets. 
                We bring you the latest smartphones, tablets, laptops, and accessories 
                from top brands at unbeatable prices.
              </p>
              <div className="flex gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 rounded-lg font-bold text-white"
                  style={{
                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                  }}
                >
                  Shop Now
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 rounded-lg font-bold border-2"
                  style={{
                    borderColor: colors.primary,
                    color: colors.primary,
                    backgroundColor: 'transparent'
                  }}
                >
                  Learn More
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <motion.img
                  whileHover={{ y: -10 }}
                  src="https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80"
                  alt="Premium Smartphones"
                  className="rounded-2xl shadow-2xl"
                />
                <motion.img
                  whileHover={{ y: -10 }}
                  transition={{ delay: 0.1 }}
                  src="https://images.unsplash.com/photo-1546868871-7041f2a55e12?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80"
                  alt="Smart Watches"
                  className="rounded-2xl shadow-2xl mt-8"
                />
                <motion.img
                  whileHover={{ y: -10 }}
                  transition={{ delay: 0.2 }}
                  src="https://images.unsplash.com/photo-1496181133206-80ce9b88a853?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80"
                  alt="Laptops"
                  className="rounded-2xl shadow-2xl"
                />
                <motion.img
                  whileHover={{ y: -10 }}
                  transition={{ delay: 0.3 }}
                  src="https://images.unsplash.com/photo-1605236453806-6ff36851218e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80"
                  alt="Tablets"
                  className="rounded-2xl shadow-2xl mt-8"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20" style={{ backgroundColor: colors.bgSecondary }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-black mb-4" style={{ color: colors.text }}>
              Why Choose JosiePhones?
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: colors.textSecondary }}>
              We are committed to providing the best shopping experience with premium products 
              and exceptional customer service.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="text-center p-6 rounded-2xl transition-all"
                style={{
                  backgroundColor: colors.bg,
                  border: `1px solid ${colors.border}`
                }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" 
                  style={{ backgroundColor: `${colors.primary}20` }}>
                  <feature.icon className="w-8 h-8" style={{ color: colors.primary }} />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: colors.text }}>
                  {feature.title}
                </h3>
                <p style={{ color: colors.textSecondary }}>
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-black mb-4" style={{ color: colors.text }}>
              Explore Our Categories
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: colors.textSecondary }}>
              Discover a wide range of premium tech products across multiple categories
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.value}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="text-center p-6 rounded-2xl cursor-pointer transition-all group"
                style={{
                  backgroundColor: colors.bgSecondary,
                  border: `2px solid ${colors.border}`
                }}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl mb-3 transition-all group-hover:scale-110"
                  style={{ backgroundColor: `${colors.primary}20` }}>
                  <category.icon className="w-7 h-7" style={{ color: colors.primary }} />
                </div>
                <h3 className="font-semibold mb-1" style={{ color: colors.text }}>
                  {category.label}
                </h3>
                <p className="text-sm font-bold" style={{ color: colors.primary }}>
                  {category.count} Products
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20" style={{ backgroundColor: colors.bgSecondary }}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="text-4xl md:text-5xl font-black mb-2"
                  style={{ color: colors.primary }}
                >
                  {stat.number}
                </motion.div>
                <p className="text-lg font-semibold" style={{ color: colors.text }}>
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-black mb-6" style={{ color: colors.text }}>
                Our Mission & Vision
              </h2>
              <div className="space-y-4">
                <p style={{ color: colors.textSecondary }}>
                  At JosiePhones, our mission is to make cutting-edge technology accessible to everyone. 
                  We believe that everyone deserves to experience the latest innovations in smartphones, 
                  tablets, and smart devices.
                </p>
                <p style={{ color: colors.textSecondary }}>
                  Our vision is to become the most trusted technology retailer in the region, 
                  known for our exceptional customer service, premium product quality, and 
                  commitment to innovation.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                alt="Our Team"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;